import React, { useState } from 'react';
import { Table, Button, Space, Tag, Typography, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, KeyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useCompanies, useDeleteCompany } from '@/hooks/useCompanies';
import type { Company } from '@/api/companies';
import ExportButtons from '@/components/common/ExportButtons';
import { extractErrorMessage } from '@/utils/helpers';

const { Title } = Typography;

const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useCompanies({ search, page });
  const deleteCompany = useDeleteCompany();

  const handleDelete = async (id: string) => {
    try {
      await deleteCompany.mutateAsync(id);
      message.success('Empresa excluída com sucesso!');
    } catch (error: any) {
      message.error(extractErrorMessage(error, 'Erro ao excluir empresa'));
    }
  };

  const exportColumns = [
    { header: 'Nome', key: 'name' },
    { header: 'CNPJ', key: 'cnpj' },
    { header: 'Status', key: 'status' },
    { header: 'E-mail', key: 'email' },
    { header: 'Telefone', key: 'phone' },
    { header: 'Usuários', key: 'active_users_count' },
    { header: 'Dispositivos', key: 'active_devices_count' },
  ];

  const columns: ColumnsType<Company> = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => record.trade_name || text,
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : status === 'INACTIVE' ? 'red' : 'orange'}>
          {status === 'ACTIVE' ? 'Ativa' : status === 'INACTIVE' ? 'Inativa' : 'Suspensa'}
        </Tag>
      ),
    },
    {
      title: 'Usuários',
      dataIndex: 'active_users_count',
      key: 'active_users_count',
    },
    {
      title: 'Dispositivos',
      dataIndex: 'active_devices_count',
      key: 'active_devices_count',
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/companies/${record.id}/edit`)}
          />
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => navigate(`/companies/${record.id}/token`)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir esta empresa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>Empresas</Title>
        <Space>
          <Input
            placeholder="Buscar empresas..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <ExportButtons
            title="Relatório de Empresas"
            columns={exportColumns}
            data={data?.results || []}
            filename="empresas"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/companies/new')}
          >
            Nova Empresa
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data?.results || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: page,
          total: data?.count || 0,
          pageSize: 20,
          onChange: setPage,
          showTotal: (total) => `Total: ${total} empresas`,
        }}
      />
    </div>
  );
};

export default CompanyList;
